import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    Asset,
    IBuildingOption,
    ISite,
    IAssetSensorMarker,
    IHierarchyLevelConfig,
    IDynamicHierarchyState,
} from '../../components/Applications/FacilityManagement/interfaces'
import { IRelation } from '../../components/DTStudio/interfaces'
import { FMTABS } from '../../constants/constants'
import { ISiteBuildingAssetCount } from '../../components/Entity/interfaces'

export const default_FM_Tab = FMTABS.MAP_VIEW
interface FacilityMonitoringState {
    isFMApplicationEnabled: boolean

    // Dynamic Hierarchy State
    hierarchyConfig: IHierarchyLevelConfig[]
    hierarchyData: Record<number, IDynamicHierarchyState>

    // Legacy Site Map State (for Map View compatibility)
    siteList: ISite[]
    siteEntityTemplateId: string
    siteLocationAttributeId: string
    siteNameAttributeId: string
    siteBuildingAssetCount: ISiteBuildingAssetCount[]

    //Buidling State
    selectedBuilding: IBuildingOption
    buildingEntityTemplateId: string
    buildingNameAttributeId: string
    buildingFloorLayoutAttributeId: string
    buildingRelations: IRelation[]
    // Buildings fetched directly when there are 0 pre-building hierarchy levels
    topLevelBuildings: IBuildingOption[]

    //Asset State
    assetRecords: Asset[]
    assetEntityTemplateId: string
    assetXCoordinateAttributeId: string
    assetYCoordinateAttributeId: string
    assetRelations: IRelation[]

    //Sensor State
    sensorEntityTemplateId: string
    sensorReadingAttributeId: string
    // sensorRecords: Sensor[]
    presentConfiguringAssetSensorMarkers: IAssetSensorMarker[]

    //Reports State
    scheduledReportsEntityTemplateId: string

    //Dashboard State
    analyticsDashboardId: string

    settingsVersion: number
}

const initialState: FacilityMonitoringState = {
    isFMApplicationEnabled: false,

    // Dynamic Hierarchy State
    hierarchyConfig: [],
    hierarchyData: {},

    // Legacy Site Map State
    siteList: [],
    siteEntityTemplateId: '',
    siteBuildingAssetCount: [],
    siteLocationAttributeId: '',
    siteNameAttributeId: '',

    //Buidling State
    selectedBuilding: {} as IBuildingOption,
    buildingEntityTemplateId: '',
    buildingNameAttributeId: '',
    buildingFloorLayoutAttributeId: '',
    buildingRelations: [],
    topLevelBuildings: [] as IBuildingOption[],

    //Asset State
    assetEntityTemplateId: '',
    assetRecords: [],
    assetXCoordinateAttributeId: '',
    assetYCoordinateAttributeId: '',
    assetRelations: [],

    //Sensor State
    sensorEntityTemplateId: '',
    sensorReadingAttributeId: '',
    presentConfiguringAssetSensorMarkers: [],

    //Reports State
    scheduledReportsEntityTemplateId: '',

    //Dashboard State
    analyticsDashboardId: '',

    settingsVersion: 0,
}

const facilityMonitoringSlice = createSlice({
    name: 'facilityMonitoring',
    initialState,
    reducers: {
        setIsFMApplicationEnabled(state, action) {
            state.isFMApplicationEnabled = action.payload
        },

        // Dynamic Hierarchy
        setHierarchyConfig(
            state,
            action: PayloadAction<IHierarchyLevelConfig[]>,
        ) {
            state.hierarchyConfig = action.payload
        },
        setHierarchyData(
            state,
            action: PayloadAction<Record<number, IDynamicHierarchyState>>,
        ) {
            state.hierarchyData = action.payload
        },
        updateHierarchyLevelData(
            state,
            action: PayloadAction<{
                levelIndex: number
                data: Partial<IDynamicHierarchyState>
            }>,
        ) {
            if (state.hierarchyData[action.payload.levelIndex]) {
                state.hierarchyData[action.payload.levelIndex] = {
                    ...state.hierarchyData[action.payload.levelIndex],
                    ...action.payload.data,
                }
            }
        },

        // Legacy Map View State
        setSelectedSite(state) {
            return state
        },
        setSiteEntityTemplateId(state, action) {
            state.siteEntityTemplateId = action.payload
        },
        setSiteList(state, action) {
            state.siteList = action.payload
        },
        setSiteBuildingAssetCount(state, action) {
            state.siteBuildingAssetCount = action.payload
        },
        setSiteLocationAttributeId(state, action) {
            state.siteLocationAttributeId = action.payload
        },
        setSiteNameAttributeId(state, action) {
            state.siteNameAttributeId = action.payload
        },

        //Building State
        setSelectedBuilding(state, action) {
            state.selectedBuilding = action.payload
        },
        setBuildingEntityTemplateId(state, action) {
            state.buildingEntityTemplateId = action.payload
        },
        setBuildingNameAttributeId(state, action) {
            state.buildingNameAttributeId = action.payload
        },
        setBuildingFloorLayoutAttributeId(state, action) {
            state.buildingFloorLayoutAttributeId = action.payload
        },
        setBuildingRelations(state, action) {
            state.buildingRelations = action.payload
        },
        setTopLevelBuildings(state, action: PayloadAction<IBuildingOption[]>) {
            state.topLevelBuildings = action.payload
        },

        //Asset State
        setAssetEntityTemplateId(state, action) {
            state.assetEntityTemplateId = action.payload
        },
        setAssetRecords(state, action) {
            state.assetRecords = action.payload
        },
        setAssetXCoordinateAttributeId(state, action) {
            state.assetXCoordinateAttributeId = action.payload
        },
        setAssetYCoordinateAttributeId(state, action) {
            state.assetYCoordinateAttributeId = action.payload
        },
        setAssetRelations(state, action) {
            state.assetRelations = action.payload
        },

        //Sensor State
        setSensorEntityTemplateId(state, action) {
            state.sensorEntityTemplateId = action.payload
        },
        setSensorReadingAttributeId(state, action) {
            state.sensorReadingAttributeId = action.payload
        },
        setPresentConfiguringAssetSensorMarkers(state, action) {
            state.presentConfiguringAssetSensorMarkers = action.payload
        },

        //Reports State
        setScheduledReportsEntityTemplateId(state, action) {
            state.scheduledReportsEntityTemplateId = action.payload
        },

        //Dashboard State
        setAnalyticsDashboardId(state, action) {
            state.analyticsDashboardId = action.payload
        },

        incrementSettingsVersion(state) {
            state.settingsVersion += 1
        },

        //Reset State on organization change
        resetFacilityMonitoringState(state) {
            state.isFMApplicationEnabled = false

            // Dynamic Hierarchy
            state.hierarchyConfig = []
            state.hierarchyData = {}

            // Legacy Map View State
            state.siteList = []
            state.siteBuildingAssetCount = []
            state.siteLocationAttributeId = ''
            state.siteNameAttributeId = ''

            //Buidling State
            state.selectedBuilding = {} as IBuildingOption
            state.buildingEntityTemplateId = ''
            state.buildingNameAttributeId = ''
            state.buildingFloorLayoutAttributeId = ''
            state.buildingRelations = []

            //Asset State
            state.assetEntityTemplateId = ''
            state.assetRecords = []
            state.assetXCoordinateAttributeId = ''
            state.assetYCoordinateAttributeId = ''
            state.assetRelations = []

            //Sensor State
            state.sensorEntityTemplateId = ''
            state.sensorReadingAttributeId = ''
            state.presentConfiguringAssetSensorMarkers = []

            //Reports State
            state.scheduledReportsEntityTemplateId = ''

            //Dashboard State
            state.analyticsDashboardId = ''
        },
    },
})

export const {
    setIsFMApplicationEnabled,
    resetFacilityMonitoringState,

    // Dynamic Hierarchy Actions
    setHierarchyConfig,
    setHierarchyData,
    updateHierarchyLevelData,

    // Legacy Map View Actions
    setSelectedSite,
    setSiteList,
    setSiteEntityTemplateId,
    setSiteLocationAttributeId,
    setSiteNameAttributeId,
    setSiteBuildingAssetCount,

    //Building State Actions
    setSelectedBuilding,
    setBuildingEntityTemplateId,
    setBuildingNameAttributeId,
    setBuildingFloorLayoutAttributeId,
    setBuildingRelations,
    setTopLevelBuildings,
    //Asset State Actions
    setAssetRecords,
    setAssetEntityTemplateId,
    setAssetXCoordinateAttributeId,
    setAssetYCoordinateAttributeId,
    setAssetRelations,
    //Sensor State Actions
    setSensorEntityTemplateId,
    setSensorReadingAttributeId,
    setPresentConfiguringAssetSensorMarkers,
    //Reports State Actions
    setScheduledReportsEntityTemplateId,
    //Dashboard State Actions
    setAnalyticsDashboardId,
    incrementSettingsVersion,
} = facilityMonitoringSlice.actions
export default facilityMonitoringSlice.reducer
